import { Dispatch, useCallback, useMemo, useState } from "react";

import { FilesPickerModalProps } from "components/modals/Files-picker/FilesPicker";
import { FileUnderScanningMessage } from "constants/deal";
import { DocumentSectionType } from "constants/documents";
import { RequestQueue, useActionHook, useModals, useNavigate, usePrompt } from "hooks";
import { debounce } from "lodash";
import { IApiServiceError, useDealIdSelector } from "store";
import { useDealActions, useDocumentsActions } from "store/actions-context";
import {
  Document,
  DocumentContent,
  DocumentReportQueries,
  DocumentReportsSectionSettings,
  DocumentSection,
  DocumentUploadType,
  IFile,
  IMoveDocumentData
} from "store/types";
import pickFiles from "utils/pick-files";
import querySelector from "utils/query-selector";

import { CreateReportSection, State } from "../Report-sections-forms/CreateReportSection";
import { CreateSectionFromMeetingContent } from "../Section-components/Meeting/CreateSectionFromMeetingContent";
import { CreateSectionManualContent } from "../Section-components/Meeting/CreateSectionManualContent";
import { DeleteMeetingSectionContent } from "../Section-components/Meeting/DeleteMeetingSectionContent";
import { reportSections, sectionTypesFile, sectionTypesQuestions } from "./Content";
import { ContentContextType } from "./content-context";

interface UseContentContextHookProps {
  document: Document<true>;
  containerRef: React.RefObject<HTMLDivElement>;
  setDocument: React.Dispatch<React.SetStateAction<Document<true>>>;
}

export const insertNewSection = (sections: DocumentSection[], newSection: DocumentSection): DocumentSection[] => {
  const updatedSections = sections.map((item) => {
    return {
      ...item,
      order: item.order >= newSection.order ? item.order + 1 : item.order
    };
  });

  return [...updatedSections, newSection].sort((sec1, sec2) => sec1.order - sec2.order);
};

export const extractSection = (sections: DocumentSection[], id: number): DocumentSection[] => {
  if (!sections.length) return [];
  const sectionToExtract = sections.find((item) => item.id === id);
  if (!sectionToExtract) return sections;

  return sections.filter((item) => item.id !== id).map((sec, index) => ({ ...sec, order: index }));
};

export const useContentContextHook = ({
  document,
  containerRef,
  setDocument
}: UseContentContextHookProps): Pick<
  ContentContextType,
  | "report_queries"
  | "filesPickerModalOpen"
  | "setFilesPickerModalOpen"
  | "isEnterDisabled"
  | "setIsEnterDisabled"
  | "createSection"
  | "deleteSection"
  | "updateSection"
  | "createQuestion"
  | "updateMeetingSection"
  | "updateQuestion"
  | "deleteQuestion"
  | "createAnswer"
  | "updateAnswer"
  | "deleteAnswer"
  | "askAIAnswer"
  | "AIAnswerSection"
  | "handleDrop"
  | "handleHTMLDrop"
  | "onMoveDocument"
  | "updateDocument"
  | "updateDocumentReportQueries"
  | "handlePaste"
  | "handleSectionFocus"
  | "handleEmptySectionFocus"
  | "handleContainerClick"
  | "cubicsQueue"
  | "tablesQueue"
  | "cashflowGraphQueue"
  | "getAnswers"
  | "filesPickerParams"
  | "sectionsToOpenChatInput"
  | "addSectionToOpenChatInput"
  | "removeSectionFromOpenChatInput"
> => {
  const dealId = useDealIdSelector();
  const isGlobalDocument = useMemo(() => !dealId, [dealId]);

  const { sections, answers } = document;
  const [filesPickerModalOpen, setFilesPickerModalOpen] = useState<number | null>(null);
  const [isEnterDisabled, setIsEnterDisabled] = useState(false);
  const [focusedSectionIndex, setFocusedSectionIndex] = useState<number>(sections.length);
  const [filesPickerParams, setFilesPickerParams] = useState<FilesPickerModalProps | null>(null);
  const [sectionsToOpenChatInput, setSectionsToOpenChatInput] = useState<Set<number>>(new Set());

  const addSectionToOpenChatInput = useCallback((sectionId: number) => {
    setSectionsToOpenChatInput((prev) => new Set(prev).add(sectionId));
  }, []);

  const removeSectionFromOpenChatInput = useCallback((sectionId: number) => {
    setSectionsToOpenChatInput((prev) => {
      const newSet = new Set(prev);
      newSet.delete(sectionId);
      return newSet;
    });
  }, []);

  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const { closeSelectDealFolderModal, errorModal, noticeModal, selectDealFolderModal } = useModals();

  const { getFilesAction, uploadFileAction, removeFilesFromStoreAction } = useDealActions();

  const {
    updateDocumentAction,
    createDocumentSectionAction,
    createDocumentQuestionAction,
    uploadGlobalDocumentFileAction,
    moveDocumentAction,
    moveMeetingSectionAction,
    createMeetingSectionAction,
    deleteDocumentSectionAction,
    updateDocumentSectionAction,
    updateDocumentQuestionAction,
    deleteDocumentQuestionAction,
    updateDocumentAnswerAction,
    deleteDocumentAnswerAction,
    createDocumentAnswerAction,
    generateAiDocumentQuestionAnswersAction,
    generateDocumentsAiQuestionAnswersAction,
    updateMeetingSectionAction
  } = useDocumentsActions();

  const setDocumentSections = useCallback<Dispatch<React.SetStateAction<DocumentSection[]>>>(
    (cbOrNewState) => {
      if (typeof cbOrNewState === "function") {
        setDocument((prev) => ({ ...prev, sections: cbOrNewState(prev.sections) }));
      } else {
        setDocument((prev) => ({ ...prev, sections: cbOrNewState }));
      }
    },
    [setDocument]
  );

  const { action: moveDocument } = useActionHook(moveDocumentAction, {
    onSuccess: async (res) => {
      closeSelectDealFolderModal();

      navigate({
        route: "documentId",
        params: {
          tab: res.deal_id ? "deals" : "general",
          document_id: res.id
        },
        ...(res.deal_id && { search: { dealId: res.deal_id } })
      });
    },
    onReject: () => {
      errorModal({ msg: "Something went wrong" });
    }
  });

  const handleFileUpload = useCallback(
    async (file: File, type: DocumentSectionType) => {
      const uploadType: DocumentUploadType = type === DocumentSectionType.Image ? "document-image" : "document-file";

      const uploadedFile =
        document.deal_id && document.section_id
          ? await uploadFileAction({
              deal_id: document.deal_id,
              section_id: document.section_id,
              file,
              upload_type: uploadType,
              onDispatch: false
            })
          : await uploadGlobalDocumentFileAction({
              file,
              upload_type: uploadType
            });

      if (uploadedFile.state === "scanning") {
        noticeModal({ msg: FileUnderScanningMessage });
      }

      return uploadedFile.id;
    },
    [
      document.deal_id,
      document.section_id,
      uploadFileAction,
      uploadGlobalDocumentFileAction,
      noticeModal,
      FileUnderScanningMessage
    ]
  );

  const calculateOrder = (sections: DocumentSection[], position: number) => {
    if (!sections.length) {
      return 0;
    } else if (!sections[position]) {
      return sections.length;
    } else {
      return position;
    }
  };

  const updateSection = useCallback<ContentContextType["updateSection"]>(
    async ({ disableSetState, ...data }) => {
      const section = await updateDocumentSectionAction(data);

      if (!disableSetState) {
        setDocumentSections((prev) => {
          return prev.map((item) => (item.id === section.id ? section : item));
        });
      }
      return section;
    },
    [setDocumentSections]
  );

  const updateMeetingSection = useCallback<ContentContextType["updateMeetingSection"]>(async (data) => {
    const section = await updateMeetingSectionAction(data);
    setDocumentSections((prev) => {
      return prev.map((item) => (item.id === section.id ? section : item));
    });
    return section;
  }, []);

  const debouncedUpdateDocumentAction = useMemo(() => debounce(updateDocumentAction, 1000), []);

  const updateDocument = useCallback(
    (data: Partial<Pick<Document, "title" | "is_private">> & Pick<Document, "id">) => {
      const { id: _id, ...updates } = data;
      setDocument((prev) => ({ ...prev, ...updates }));
      debouncedUpdateDocumentAction(data);
    },
    [debouncedUpdateDocumentAction, setDocument]
  );

  const updateDocumentReportQueries = useCallback(
    async (data: DocumentReportQueries) => {
      const { report_data } = await updateDocumentAction({
        id: document.id,
        report_data: { ...document.report_data, queries: data }
      });
      setDocument((prev) => ({
        ...prev,
        report_data
      }));
      return report_data;
    },
    [document.id, updateDocumentAction]
  );

  const handleCreateMeetingSection = async ({ isAiMeeting, position }: { isAiMeeting: boolean; position: number }) => {
    if (isAiMeeting) {
      const meetingId = await openPrompt({
        Component: CreateSectionFromMeetingContent
      });
      if (!meetingId) return;
      const section = await moveMeetingSectionAction({
        document_id: document.id,
        meeting_id: meetingId,
        document_section_order: calculateOrder(sections, position)
      });
      setDocumentSections((prev) => insertNewSection(prev, section));
      return section;
    } else {
      const meetingData = await openPrompt({
        Component: CreateSectionManualContent,
        props: {}
      });
      if (!meetingData) return;
      const section = await createMeetingSectionAction({
        document_id: document.id,
        document_section_order: position,
        ...meetingData
      });
      setDocumentSections((prev) => insertNewSection(prev, section));
      return section;
    }
  };

  const handleCreateAnalyzeFundTableSection = async (type: DocumentSectionType, position: number) => {
    const createAnalyzeFundSection = async (fileId: number) => {
      const section = await createDocumentSectionAction({
        document_id: document.id,
        order: calculateOrder(sections, position),
        text: "",
        type,
        file_id: fileId
      });
      setDocumentSections((prev) => insertNewSection(prev, section));
    };

    setFilesPickerParams({
      filesPickerModalOpen: true,
      onClose: () => setFilesPickerParams(null),
      currentFiles: [],
      filesPickerSubmit: async (data: { filesIds?: number[]; attachments: File[]; files?: IFile[] }) => {
        if (data.attachments.length) {
          const fileId = await handleFileUpload(data.attachments[0], type);
          await createAnalyzeFundSection(fileId);
          return;
        }
        const fileId = isGlobalDocument ? data.files?.[0]?.id : data.filesIds?.[0];
        if (fileId) {
          await createAnalyzeFundSection(fileId);
          return;
        }
      },
      title: "Analyze Fund PDF",
      workspaceTitle: "Select or upload file",
      singleFileSelection: true,
      dealId: document.deal_id,
      getFilesOutsideDeal: isGlobalDocument
    });
    return;
  };

  const createSection: ContentContextType["createSection"] = useCallback(
    async ({ position, type, text, files, question, isAiMeeting }) => {
      try {
        if (type === DocumentSectionType.AnalyzeFundTable) {
          await handleCreateAnalyzeFundTableSection(type, position);
          return;
        }
        const hasDocumentFilters: boolean = Boolean(document.report_data?.queries);
        let fileId: number | null = null;
        let reportData:
          | (Partial<Pick<State, "queries">> & {
              settings: DocumentReportsSectionSettings[keyof DocumentReportsSectionSettings];
            })
          | null = null;
        let documentReportData: DocumentContent["report_data"] | null = null;

        if (type === DocumentSectionType.Meeting && typeof isAiMeeting === "boolean") {
          const section = await handleCreateMeetingSection({ isAiMeeting, position });
          if (!section) return;
          return section;
        }

        if (sectionTypesFile.includes(type)) {
          const acceptedFilesExtensions =
            type === DocumentSectionType.Image ? ["image/jpg", "image/jpeg", "image/png"] : ["application/pdf"];
          const fileList = files || (await pickFiles({ accept: acceptedFilesExtensions.join(",") }));
          if (!fileList?.length) return; // if user cancel pick files

          fileId = await handleFileUpload(fileList[0], type);
        }

        if (reportSections.includes(type)) {
          const data = await openPrompt({
            Component: CreateReportSection,
            props: {
              sectionType: type,
              documentId: document.id,
              order: position,
              forceCreateQueries: !hasDocumentFilters
            }
          });

          if (!data) return; // if user cancel prompt

          // Add queries to section if document has queries and user created new queries for section
          reportData = {
            ...(hasDocumentFilters && !data.pickFiltersSet.isUseCurrent && { queries: data.queries }),
            ...(type === DocumentSectionType.CashflowGraph && { queries: data.queries }),
            settings: data[type as keyof DocumentReportsSectionSettings]
          };

          // Send queries as Document queries if there weren't queries in document
          if (!hasDocumentFilters && data.queries) {
            const { report_data } = await updateDocumentAction({
              id: document.id,
              report_data: {
                ...document.report_data,
                queries: data?.queries
              }
            });
            documentReportData = report_data;
          }
        }

        const section = await createDocumentSectionAction({
          document_id: document.id,
          order: calculateOrder(sections, position),
          text,
          type,
          ...(fileId && { file_id: fileId }),
          ...(question && { parameters: { question } }),
          ...(reportData && { report_data: reportData })
        });

        setDocument((prev) => ({
          ...prev,
          ...(documentReportData && { report_data: documentReportData }),
          sections: insertNewSection(prev.sections, section)
        }));

        if (section.type === DocumentSectionType.AIQuestion) {
          setFilesPickerModalOpen(section.id);
        }

        if (section.type === DocumentSectionType.Text && containerRef.current) {
          // setTimeout to focus input after DOM is updated
          setTimeout(() => {
            const { current } = containerRef;
            if (!current) return;
            const sectionElement = current.querySelector(`[data-section-id="${section.id}"]`);
            if (!sectionElement) return;
            const editableContent = sectionElement.querySelector(".tiptap") as HTMLElement;
            if (!editableContent) return;
            editableContent.focus();
          }, 100);
        }

        if (sectionTypesFile.includes(section.type) && document.deal_id) {
          getFilesAction(document.deal_id);
        }

        if (sectionTypesQuestions.includes(section.type)) {
          createQuestion({
            section_id: section.id,
            documentId: document.id
          });
        }
        return section;
      } catch (error) {
        const firstValidationError = Object.values((error as IApiServiceError)?.errors)?.[0]?.[0];
        errorModal({
          msg: firstValidationError || "Something went wrong during section creation"
        });
      }
    },
    [document, sections, errorModal, openPrompt]
  );

  const deleteSection = useCallback<ContentContextType["deleteSection"]>(
    async ({ sectionId, isAiMeeting }) => {
      if (isAiMeeting) {
        const isConfirmed = await openPrompt({
          Component: DeleteMeetingSectionContent,
          props: {
            is_ai: isAiMeeting
          }
        });
        if (!isConfirmed) return;
      }
      let removeDocumentReportData = false;

      const section = sections.find((section) => section.id === sectionId);
      if (!section) return;

      // Delete all files that were attached to the section.
      const filesIdsToDelete: number[] = [];

      section.ai_files?.forEach((file) => {
        if (file.upload_type === "document-ai-file") {
          filesIdsToDelete.push(file.id);
        }
      });
      if ("file" in section && section.file) {
        filesIdsToDelete.push(section.file.id);
      }

      await deleteDocumentSectionAction(sectionId);

      if (filesIdsToDelete.length) {
        removeFilesFromStoreAction(filesIdsToDelete);
      }

      setDocument((prev) => {
        const newSections = extractSection(prev.sections || [], sectionId);

        if (!newSections.some((section) => reportSections.includes(section.type)) && prev.report_data) {
          removeDocumentReportData = true;
        }

        return {
          ...prev,
          sections: newSections,
          questions: prev.questions?.filter((item) => item.section_id !== sectionId),
          answers: prev.answers?.filter((item) => item.section_id !== sectionId),
          ...(prev.files?.length && {
            files: prev.files.filter((item) => item.document_section_id !== sectionId)
          })
        };
      });

      // Remove reports data of no report sections left
      if (removeDocumentReportData) {
        await updateDocumentAction({
          id: document.id,
          report_data: null
        });

        setDocument((prev) => ({
          ...prev,
          report_data: null
        }));
      }
    },
    [setDocument, sections]
  );

  const createQuestion = useCallback<ContentContextType["createQuestion"]>(
    async (data) => {
      const question = await createDocumentQuestionAction(data);
      setDocument((prev) => ({
        ...prev,
        questions: [...(prev.questions || []), question]
      }));
      return question;
    },
    [setDocument]
  );

  const updateQuestion = useCallback<ContentContextType["updateQuestion"]>(
    async (data) => {
      const question = await updateDocumentQuestionAction(data);
      setDocument((prev) => ({
        ...prev,
        questions: prev.questions.map((item) => (item.id === question.id ? question : item))
      }));
      return question;
    },
    [setDocument]
  );

  const deleteQuestion = useCallback<ContentContextType["deleteQuestion"]>(
    async (questionId) => {
      await deleteDocumentQuestionAction(questionId);
      setDocument((prev) => ({
        ...prev,
        questions: prev.questions.filter((item) => item.id !== questionId),
        answers: prev.answers.filter((item) => item.question_id !== questionId)
      }));
    },
    [setDocument]
  );

  const createAnswer = useCallback<ContentContextType["createAnswer"]>(
    async (data) => {
      const answer = await createDocumentAnswerAction(data);
      setDocument((prev) => ({
        ...prev,
        answers: [...(prev.answers || []), answer]
      }));
      return answer;
    },
    [setDocument]
  );

  const updateAnswer = useCallback<ContentContextType["updateAnswer"]>(
    async ({ disableSetState, ...data }) => {
      const answer = await updateDocumentAnswerAction(data);
      if (!disableSetState) {
        setDocument((prev) => ({
          ...prev,
          answers: prev.answers.map((item) => (item.id === answer.id ? answer : item))
        }));
      }
      return answer;
    },
    [setDocument]
  );

  const deleteAnswer = useCallback<ContentContextType["deleteAnswer"]>(
    async (answerId) => {
      await deleteDocumentAnswerAction(answerId);
      setDocument((prev) => ({
        ...prev,
        answers: prev.answers.filter((item) => item.id !== answerId)
      }));
    },
    [setDocument]
  );

  const askAIAnswer = useCallback<ContentContextType["askAIAnswer"]>(
    async (data) => {
      const { question, answer } = await generateAiDocumentQuestionAnswersAction(data);

      setDocument((prev) => ({
        ...prev,
        //sort questions by created_at and filter out the question that is being updated
        questions: [...(prev.questions.filter((item) => item.id !== question.id) || []), question].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ),
        //remove old answer from ai
        answers: [
          ...(prev.answers?.filter((item) => item.question_id !== question.id || item.user_id !== null) || []),
          answer
        ]
      }));

      return { question, answer };
    },
    [setDocument]
  );

  const AIAnswerSection = useCallback<ContentContextType["AIAnswerSection"]>(
    async (data) => {
      const section = await generateDocumentsAiQuestionAnswersAction(data);

      setDocument((prev) => ({
        ...prev,
        sections: prev.sections.map((item) => (item.id === section.id ? section : item))
      }));

      return section;
    },
    [setDocument]
  );

  // const handleSelectSection = useCallback(
  //   (sectionId: number, selected: boolean) => {
  //     onSelectSection(sectionId, selected);
  //   },
  //   [onSelectSection]
  // );

  // const handleShare = useCallback(
  //   (sectionId: number) => {
  //     onShowSharePopup(sectionId);
  //   },
  //   [onShowSharePopup]
  // );

  // const handleMultiShare = useCallback(() => {
  //   setSelectionMode(true);
  // }, [setSelectionMode]);

  const handleDrop = useCallback(
    (files: FileList) => {
      const filesArray = Array.from(files);
      filesArray.forEach((file) => {
        if (file.type.startsWith("image/")) {
          createSection({ position: sections.length, type: DocumentSectionType.Image, text: "", files });
        } else {
          createSection({ position: sections.length, type: DocumentSectionType.File, text: "", files });
        }
      });
    },
    [sections.length, createSection]
  );

  const handleHTMLDrop = useCallback(
    (html: string) => {
      createSection({
        position: sections.length,
        type: DocumentSectionType.Text,
        text: `<p>${html.replaceAll(/<meta\b[^>]*?>/gi, "")}</p>`
      });
    },
    [sections.length, createSection]
  );

  const onMoveDocument = useCallback(() => {
    selectDealFolderModal({
      isOpen: true,
      title: "Move document",
      isGeneralDocument: true,
      onSubmit: ({ deal, selectedFolder }) => {
        if (!deal || !selectedFolder) return;
        const resultDataToSubmit: IMoveDocumentData = {
          documentId: document.id,
          deal_id: deal.id
        };

        if (selectedFolder.id !== 0) {
          resultDataToSubmit.section_id = selectedFolder.id;
        }

        if (selectedFolder.id === 0) {
          resultDataToSubmit.section_title = selectedFolder.title;
        }

        if (selectedFolder.id === 0 && selectedFolder.parent_id) {
          resultDataToSubmit.parent_section_id = selectedFolder.parent_id;
        }

        moveDocument(resultDataToSubmit);
      }
    });
  }, [document, selectDealFolderModal, moveDocument]);

  const handleSectionFocus = useCallback((index: number) => {
    setFocusedSectionIndex(index);
  }, []);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent, containerRef: React.RefObject<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target === containerRef.current) {
        setFocusedSectionIndex(sections.length);
      }
    },
    [sections.length]
  );

  const handleEmptySectionFocus = useCallback(() => {
    setFocusedSectionIndex(0);
  }, []);

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      event.preventDefault();

      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (!file) continue;

          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);

          createSection({
            position: focusedSectionIndex,
            type: DocumentSectionType.Image,
            files: dataTransfer.files
          });
        }
      }
    },
    [focusedSectionIndex, createSection]
  );

  const cubicsQueue = useMemo(() => new RequestQueue(), []);

  const tablesQueue = useMemo(() => new RequestQueue(), []);

  const cashflowGraphQueue = useMemo(() => new RequestQueue(), []);

  const getAnswers = useCallback<ContentContextType["getAnswers"]>(
    (queries) => {
      if (!queries) return answers;
      return querySelector(answers, queries);
    },
    [answers]
  );

  // export const useDocumentAnswersSelect = ({
  //   documentId,
  //   ...where
  // }: { documentId: number } & Partial<Pick<DocumentAnswer, "question_id" | "parent_answer_id">>): DocumentAnswer[] => {
  //   return useContextSelector(StoreContext, (state) => {
  //     const document = state.documents.find((doc) => doc.id === documentId);
  //     if (!document?.answers?.length) return [];
  //     return querySelector(document.answers, where);
  //   });
  // };

  return {
    // Filters
    report_queries: document.report_data?.queries || null,

    // Files picker state
    filesPickerModalOpen,
    setFilesPickerModalOpen,

    // Enter key state
    isEnterDisabled,
    setIsEnterDisabled,

    // Section creation handlers
    createSection,
    deleteSection,
    updateSection,
    updateMeetingSection,
    // handleFileUpload,

    // Questions
    createQuestion,
    updateQuestion,
    deleteQuestion,

    // Answers
    createAnswer,
    updateAnswer,
    deleteAnswer,
    askAIAnswer,
    AIAnswerSection,
    // // Selection state
    // selectionMode,
    // setSelectionMode,
    // selectedSections,
    // handleSelectSection,

    // // Share handlers
    // handleShare,
    // handleMultiShare,

    // Drop handlers
    handleDrop,
    handleHTMLDrop,
    onMoveDocument,

    // Document update
    updateDocument,
    updateDocumentReportQueries,

    // Paste handlers
    handlePaste,

    // Focus handlers
    handleSectionFocus,
    handleEmptySectionFocus,
    handleContainerClick,
    cubicsQueue,
    tablesQueue,
    cashflowGraphQueue,
    getAnswers,

    // Files picker params
    filesPickerParams,

    // ChatInput tracking
    sectionsToOpenChatInput,
    addSectionToOpenChatInput,
    removeSectionFromOpenChatInput
  };
};
